import { useEffect, useState, useRef } from 'react';
import MaterialsService, { type SharedMaterial } from '../services/MaterialsService';
import ProfilesService from '../services/ProfilesService';
import AdminFunctions from '../services/AdminFunctionsService';
import LoginHistoryModal from './LoginHistoryModal';
import LoginHistoryService from '../services/LoginHistoryService';
import FirestoreStatsWidget from './FirestoreStatsWidget';
import { isValidEmail } from '../utils/validation';
import { translateFirebaseError } from '../utils/firebaseErrors';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { auth } from '../config/firebase';

interface AdminPanelProps {
  isOpen: boolean
  onClose: () => void
  // New: admin auth indicator and logout handler
  isLoggedIn?: boolean
  onLogout?: () => void
}

type AdminView = 'dashboard' | 'add-fireplace' | 'settings' | 'users' | 'materials'

const AdminPanel = ({ isOpen, onClose, isLoggedIn = false, onLogout }: AdminPanelProps) => {
  const [currentView, setCurrentView] = useState<AdminView>('dashboard')
  
  // Reset view to dashboard when panel opens
  useEffect(() => {
    if (isOpen) {
      setCurrentView('dashboard')
    }
  }, [isOpen])
  

  // Removed add-fireplace form handlers (placeholder view only now)

  const renderDashboard = () => (
    <div className="admin-dashboard">
      <h3>Panel Administracyjny</h3>
      <div className="admin-tiles">
        <div className="admin-tile" onClick={() => setCurrentView('settings')}>
          <div className="tile-icon">⚙️</div>
          <div className="tile-title">Ustawienia</div>
          <div className="tile-description">Konfiguracja systemu</div>
        </div>
        
        <div className="admin-tile" onClick={() => setCurrentView('users')}>
          <div className="tile-icon">👥</div>
          <div className="tile-title">Użytkownicy</div>
          <div className="tile-description">Zarządzanie kontami</div>
        </div>
        
        <div className="admin-tile" onClick={() => setCurrentView('materials')}>
          <div className="tile-icon">📁</div>
          <div className="tile-title">Materiały Wspólne</div>
          <div className="tile-description">Pliki i zasoby</div>
        </div>
        
        <div className="admin-tile" onClick={() => setCurrentView('add-fireplace')}>
          <div className="tile-icon">🔥</div>
          <div className="tile-title">Dodaj Kominek</div>
          <div className="tile-description">Nowy wkład kominkowy</div>
        </div>
      </div>
    </div>
  )

  const renderAddFireplace = () => (
    <div className="admin-add-fireplace">
      <div className="admin-view-header">
        <button className="btn-back" onClick={() => setCurrentView('dashboard')}>
          ← Powrót do panelu głównego
        </button>
        <h3>Dodaj Kominek</h3>
      </div>
      <div className="placeholder-content">
        <p>W przyszłości w tym miejscu będzie można dodawać, usuwać i modyfikować kominki.</p>
      </div>
    </div>
  )

  // placeholder helper removed (replaced by concrete views)

  const [materials, setMaterials] = useState<SharedMaterial[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [users, setUsers] = useState<any[]>([])
  const [showDeletedUsers, setShowDeletedUsers] = useState(false)
  const [showCreateUser, setShowCreateUser] = useState(false)
  const [createForm, setCreateForm] = useState({ email: '', username: '', companyName: '', password: '' })
  const [creating, setCreating] = useState(false)
  const [createErrors, setCreateErrors] = useState<{ email?: string; username?: string }>({})
  const [createChecking, setCreateChecking] = useState<{ email?: boolean; username?: boolean }>({})
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState<any[]>([]) 
  const [isSearching, setIsSearching] = useState(false)
  const [isInSearchMode, setIsInSearchMode] = useState(false)
  const [searchResultsSource, setSearchResultsSource] = useState<'cache' | 'database' | null>(null)
  
  // Pagination state
  const [usersLoaded, setUsersLoaded] = useState(false)
  const [usersLimit, setUsersLimit] = useState(100)
  const [materialsLimit, setMaterialsLimit] = useState(50)
  const [hasMoreUsers, setHasMoreUsers] = useState(true)
  const [hasMoreMaterials, setHasMoreMaterials] = useState(true)
  const [loadingMoreUsers, setLoadingMoreUsers] = useState(false)
  const [loadingMoreMaterials, setLoadingMoreMaterials] = useState(false)
  
  // Login history modal state
  const [showLoginHistory, setShowLoginHistory] = useState(false)
  const [selectedUser, setSelectedUser] = useState<{ id: string; username: string } | null>(null)

  const refreshMaterials = async () => {
    try {
      console.log('🔄 Pobieranie materiałów...')
      const list = await MaterialsService.listSharedMaterials(materialsLimit)
      console.log('✅ Pobrano materiały:', list)
      setMaterials(list)
      setHasMoreMaterials(list.length === materialsLimit)
    } catch (e) {
      console.error('❌ Błąd pobierania materiałów:', e)
    }
  }

  const refreshUsers = async (includeDeleted?: boolean) => {
    try {
      const shouldIncludeDeleted = includeDeleted !== undefined ? includeDeleted : showDeletedUsers
      console.log('🔄 Pobieranie użytkowników... (includeDeleted:', shouldIncludeDeleted, ')')
      const list = await ProfilesService.listAllProfiles(shouldIncludeDeleted, usersLimit)
      console.log('✅ Pobrano użytkowników:', list)
      // Ukryj konta admin z listy użytkowników w panelu
      const filteredList = list.filter(p => p.role !== 'admin')
      console.log('📋 Przefiltrowana lista:', filteredList)
      setUsers(filteredList.map(p => ({ 
        id: p.uid, 
        email: p.email, 
        username: p.username, 
        companyName: p.companyName,
        role: p.role,
        deleted: p.deleted || false
      })))
      setHasMoreUsers(list.length === usersLimit)
      setUsersLoaded(true)
    } catch (e) {
      console.error('❌ Błąd pobierania użytkowników:', e)
    }
  }

  const loadMoreUsers = async () => {
    if (loadingMoreUsers || !hasMoreUsers) return
    
    try {
      setLoadingMoreUsers(true)
      const newLimit = usersLimit + 100
      const shouldIncludeDeleted = showDeletedUsers
      const list = await ProfilesService.listAllProfiles(shouldIncludeDeleted, newLimit)
      
      const filteredList = list.filter(p => p.role !== 'admin')
      setUsers(filteredList.map(p => ({ 
        id: p.uid, 
        email: p.email, 
        username: p.username, 
        companyName: p.companyName,
        role: p.role,
        deleted: p.deleted || false
      })))
      
      setUsersLimit(newLimit)
      setHasMoreUsers(list.length === newLimit)
    } catch (e) {
      console.error('❌ Błąd ładowania więcej użytkowników:', e)
    } finally {
      setLoadingMoreUsers(false)
    }
  }

  const loadMoreMaterials = async () => {
    if (loadingMoreMaterials || !hasMoreMaterials) return
    
    try {
      setLoadingMoreMaterials(true)
      const newLimit = materialsLimit + 50
      const list = await MaterialsService.listSharedMaterials(newLimit)
      
      setMaterials(list)
      setMaterialsLimit(newLimit)
      setHasMoreMaterials(list.length === newLimit)
    } catch (e) {
      console.error('❌ Błąd ładowania więcej materiałów:', e)
    } finally {
      setLoadingMoreMaterials(false)
    }
  }

  const searchUsers = async (term: string) => {
    if (!term.trim()) {
      setIsInSearchMode(false)
      setSearchResults([])
      setSearchResultsSource(null)
      return
    }
    
    try {
      setIsSearching(true)
      setIsInSearchMode(true)
      const searchLower = term.toLowerCase()
      
      // KROK 1: Przeszukaj najpierw cache (załadowanych użytkowników)
      console.log('🔍 Przeszukiwanie cache (' + users.length + ' użytkowników)...')
      const cacheResults = users.filter(user => {
        return user.username?.toLowerCase().includes(searchLower) || 
               user.email?.toLowerCase().includes(searchLower) ||
               user.companyName?.toLowerCase().includes(searchLower)
      })
      
      console.log('✅ Znaleziono w cache:', cacheResults.length)
      
      // Jeśli znaleziono wyniki w cache, użyj ich
      if (cacheResults.length > 0) {
        console.log('🏆 Używam wyników z cache (bez zapytania do bazy)')
        setSearchResults(cacheResults)
        setSearchResultsSource('cache')
        setIsSearching(false)
        return
      }
      
      // KROK 2: Jeśli cache nie zawiera wyników, przeszukaj pełną bazę
      console.log('🔍 Cache pusty, przeszukiwanie pełnej bazy...')
      const dbResults = await ProfilesService.searchProfiles(term, showDeletedUsers)
      console.log('✅ Znaleziono w bazie:', dbResults.length)
      
      // Mapuj do formatu używanego w AdminPanel
      const mappedResults = dbResults.map(p => ({
        id: p.uid,
        email: p.email,
        username: p.username,
        companyName: p.companyName,
        role: p.role,
        deleted: p.deleted || false
      }))
      
      setSearchResults(mappedResults)
      setSearchResultsSource('database')
    } catch (e) {
      console.error('❌ Błąd wyszukiwania:', e)
      setSearchResults([])
    } finally {
      setIsSearching(false)
    }
  }

  // Ręczne wyszukiwanie tylko po naciśnięciu Enter lub przycisku
  const handleSearchSubmit = (e?: React.FormEvent) => {
    e?.preventDefault()
    searchUsers(searchTerm)
  }
  
  // Wyczyść wyniki gdy search term się wyczyści
  useEffect(() => {
    if (!searchTerm.trim()) {
      setIsInSearchMode(false)
      setSearchResults([])
      setSearchResultsSource(null)
    }
  }, [searchTerm])

  // Ref żeby śledzić co już załadowaliśmy i uniknąć ponownych zapytań
  const loadedViews = useRef<Set<string>>(new Set())
  
  useEffect(() => {
    if (isOpen) {
      console.log('🔄 AdminPanel otworzony, currentView:', currentView)
      
      // Sprawdź czy już ładowaliśmy dane dla tego widoku w tej sesji
      if (loadedViews.current.has(currentView)) {
        console.log('📋 Data already loaded for view:', currentView)
        return
      }
      
      // Ładuj dane tylko dla aktualnie wybranego widoku
      if (currentView === 'materials') {
        console.log('🎨 Ładowanie materiałów...')
        refreshMaterials()
        loadedViews.current.add('materials')
      }
      // Dashboard i inne widoki nie wymagają automatycznego ładowania danych
    }
    
    // Resetuj cache gdy panel się zamyka
    if (!isOpen) {
      loadedViews.current.clear()
      setUsersLoaded(false)
      console.log('🧹 Cleared loaded views cache and reset usersLoaded')
    }
  }, [isOpen, currentView])



  const onUploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setIsUploading(true)
    try {
      await MaterialsService.uploadSharedMaterial(file, 'admin')
      await refreshMaterials()
    } catch (err) {
      alert('Błąd podczas uploadu: ' + (err as any)?.message)
    } finally {
      setIsUploading(false)
      e.currentTarget.value = ''
    }
  }

  const onDeleteMaterial = async (m: SharedMaterial) => {
    if (!confirm(`Usunąć materiał "${m.name}"?`)) return
    try {
      await MaterialsService.deleteSharedMaterial(m.id!, m.storagePath)
      await refreshMaterials()
    } catch (err) {
      alert('Błąd podczas usuwania: ' + (err as any)?.message)
    }
  }

  const renderSettingsView = () => (
    <div className="admin-settings">
      <div className="admin-view-header">
        <button className="btn-back" onClick={() => setCurrentView('dashboard')}>
          ← Powrót do panelu głównego
        </button>
        <h3>Ustawienia</h3>
      </div>
      <div className="placeholder-content">
        <p>Brak dodatkowych ustawień.</p>
      </div>
    </div>
  )

  const renderUsersView = () => (
    <div className="admin-settings">
      <div className="admin-view-header">
        <button className="btn-back" onClick={() => setCurrentView('dashboard')}>
          ← Powrót do panelu głównego
        </button>
        <h3>Użytkownicy</h3>
      </div>
      <div className="admin-form" style={{ maxWidth: 720 }}>
        <div className="form-group" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontWeight: 600 }}>Dodaj nowego użytkownika</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>Utwórz konto klienta z e‑mailem, loginem i hasłem</div>
          </div>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowCreateUser(v => !v)}
          >
            {showCreateUser ? 'Ukryj formularz' : 'Pokaż formularz'}
          </button>
        </div>

        {showCreateUser && (
          <div style={{ display: 'grid', gap: 12, background: '#f9fafb', padding: 12, borderRadius: 8, marginTop: 8 }}>
            <div className="form-group">
              <label>E‑mail</label>
              <input
                type="email"
                value={createForm.email}
                onChange={e => { setCreateForm({ ...createForm, email: e.target.value }); setCreateErrors(prev => ({ ...prev, email: undefined })) }}
                onBlur={async () => {
                  const v = createForm.email.trim()
                  if (!v) return
                  if (!isValidEmail(v)) { setCreateErrors(prev => ({ ...prev, email: 'Nieprawidłowy format e‑mail.' })); return }
                  setCreateChecking(prev => ({ ...prev, email: true }))
                  try {
                    const taken = await ProfilesService.isEmailTaken(v)
                    setCreateErrors(prev => ({ ...prev, email: taken ? 'Adres e‑mail jest już zajęty.' : undefined }))
                  } finally { setCreateChecking(prev => ({ ...prev, email: false })) }
                }}
                required
                pattern="[^\s@]+@[^\s@]+\.[^\s@]{2,}"
              />
              {createErrors.email && <div style={{ color: '#b91c1c', fontSize: 12 }}>{createErrors.email}</div>}
            </div>
            <div className="form-group">
              <label>Login</label>
              <input
                type="text"
                value={createForm.username}
                onChange={e => { setCreateForm({ ...createForm, username: e.target.value }); setCreateErrors(prev => ({ ...prev, username: undefined })) }}
                onBlur={async () => {
                  const v = createForm.username.trim()
                  if (!v) return
                  setCreateChecking(prev => ({ ...prev, username: true }))
                  try {
                    const taken = await ProfilesService.isUsernameTaken(v)
                    setCreateErrors(prev => ({ ...prev, username: taken ? 'Login jest już zajęty.' : undefined }))
                  } finally { setCreateChecking(prev => ({ ...prev, username: false })) }
                }}
                required
              />
              {createErrors.username && <div style={{ color: '#b91c1c', fontSize: 12 }}>{createErrors.username}</div>}
            </div>
            <div className="form-group">
              <label>Nazwa firmy (opcjonalnie)</label>
              <input 
                type="text" 
                value={createForm.companyName} 
                onChange={e => setCreateForm({ ...createForm, companyName: e.target.value })} 
                placeholder="np. ABC Sp. z o.o."
              />
            </div>
            <div className="form-group">
              <label>Hasło</label>
              <input type="password" value={createForm.password} onChange={e => setCreateForm({ ...createForm, password: e.target.value })} required />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-secondary" type="button" onClick={() => setShowCreateUser(false)}>Anuluj</button>
              <button
                className="btn btn-primary"
                type="button"
                disabled={
                  creating || !!createErrors.email || !!createErrors.username || !!createChecking.email || !!createChecking.username || !isValidEmail(createForm.email) || !createForm.username.trim() || !createForm.password
                }
                onClick={async () => {
                  if (!createForm.email || !createForm.username || !createForm.password) return
                  if (!isValidEmail(createForm.email)) { alert('Podaj poprawny adres e‑mail.'); return }
                  setCreating(true)
                  let createdUserId: string | null = null
                  
                  try {
                    // Uniqueness checks are also enforced in the service; this is an extra early guard
                    if (await ProfilesService.isEmailTaken(createForm.email.trim())) { alert('Adres e‑mail jest już zajęty.'); setCreating(false); return }
                    if (await ProfilesService.isUsernameTaken(createForm.username.trim())) { alert('Login jest już zajęty.'); setCreating(false); return }
                    
                    try {
                      // Try Cloud Function first (if available)
                      console.log('🔥 Próba tworzenia użytkownika przez Cloud Function...')
                      const { uid } = await AdminFunctions.createAuthUser({ email: createForm.email.trim(), password: createForm.password, username: createForm.username.trim(), role: 'client' })
                      console.log('✅ Cloud Function - utworzono użytkownika Auth:', uid)
                      await ProfilesService.ensureProfile(uid, createForm.email.trim(), createForm.username.trim(), 'client', createForm.companyName.trim() || undefined)
                      console.log('✅ Cloud Function - utworzono profil Firestore')
                      createdUserId = uid
                    } catch (funcErr) {
                      // Fallback: create Firebase Auth user directly
                      console.warn('⚠️ Cloud Functions niedostępne, tworzenie użytkownika bezpośrednio przez Firebase Auth:', funcErr)
                      
                      try {
                        console.log('🔥 Tworzenie użytkownika bezpośrednio przez Firebase Auth...')
                        const cred = await createUserWithEmailAndPassword(auth, createForm.email.trim(), createForm.password)
                        const user = cred.user
                        console.log('✅ Firebase Auth - utworzono użytkownika:', user.uid)
                        
                        // Set displayName
                        try {
                          await updateProfile(user, { displayName: createForm.username.trim() })
                          console.log('✅ Firebase Auth - zaktualizowano displayName')
                        } catch (profileErr) {
                          console.warn('⚠️ Nie udało się zaktualizować displayName:', profileErr)
                        }
                        
                        // Create profile in Firestore
                        console.log('🔥 Tworzenie profilu w Firestore...')
                        await ProfilesService.ensureProfile(user.uid, createForm.email.trim(), createForm.username.trim(), 'client', createForm.companyName.trim() || undefined)
                        console.log('✅ Firestore - utworzono profil')
                        createdUserId = user.uid
                      } catch (directErr) {
                        console.error('❌ Błąd bezpośredniego tworzenia użytkownika:', directErr)
                        throw directErr
                      }
                    }
                    
                    // Dodaj tylko nowego użytkownika do listy (jeśli lista jest już załadowana)
                    if (usersLoaded && createdUserId) {
                      const newUser = {
                        id: createdUserId,
                        email: createForm.email.trim(),
                        username: createForm.username.trim(),
                        companyName: createForm.companyName.trim() || undefined,
                        role: 'client' as const,
                        deleted: false
                      }
                      setUsers(prev => [newUser, ...prev])
                    }
                    
                    setCreateForm({ email: '', username: '', companyName: '', password: '' })
                    setShowCreateUser(false)
                  } catch (err) {
                    alert(translateFirebaseError(err))
                  } finally {
                    setCreating(false)
                  }
                }}
              >
                {creating ? 'Tworzenie…' : 'Utwórz'}
              </button>
            </div>
          </div>
        )}

        {/* Checkbox pokaż usuniętych - pokazuje się zawsze, ale poniżej formularza gdy jest widoczny */}
        <div style={{ marginTop: showCreateUser ? 12 : 16, marginBottom: 8 }}>
          <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showDeletedUsers}
              onChange={(e) => {
                const newValue = e.target.checked
                setShowDeletedUsers(newValue)
                // Odśwież tylko jeśli użytkownicy są już załadowani
                if (usersLoaded) {
                  refreshUsers(newValue)
                }
              }}
              style={{ marginRight: '6px' }}
            />
            Pokaż usuniętych
          </label>
        </div>

        <div style={{ marginTop: 16 }}>
          {/* Pole wyszukiwania */}
          <form onSubmit={handleSearchSubmit} style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                placeholder="Szukaj użytkowników po nazwie lub e-mailu..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleSearchSubmit()
                  }
                }}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: '#fff'
                }}
              />
              <button
                type="submit"
                disabled={isSearching || !searchTerm.trim()}
                style={{
                  padding: '8px 16px',
                  backgroundColor: isSearching || !searchTerm.trim() ? '#ccc' : '#d4af37',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isSearching || !searchTerm.trim() ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                  whiteSpace: 'nowrap'
                }}
              >
                {isSearching ? '🔍 Szukam...' : '🔍 Szukaj'}
              </button>
            </div>
          </form>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontWeight: 600 }}>Lista użytkowników</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {!usersLoaded ? (
                <button
                  onClick={() => {
                    refreshUsers()
                    setUsersLoaded(true)
                  }}
                  style={{
                    padding: '8px 16px',
                    border: 'none',
                    borderRadius: '0px',
                    backgroundColor: '#4F46E5',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#4338CA'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#4F46E5'}
                >
                  📋 Pokaż użytkowników
                </button>
              ) : (
                <button
                  onClick={() => refreshUsers()}
                  style={{
                    padding: '8px 16px',
                    border: 'none',
                    borderRadius: '0px',
                    backgroundColor: '#D2B48C',
                    color: '#333',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#C19A6B'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#D2B48C'}
                >
                  🔄 Odśwież listę
                </button>
              )}
            </div>
          </div>
          {!usersLoaded ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px 20px', 
              color: '#6b7280', 
              backgroundColor: '#f9fafb', 
              borderRadius: '8px',
              border: '2px dashed #d1d5db'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>👥</div>
              <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>Użytkownicy nie zostali jeszcze załadowani</div>
              <div style={{ fontSize: '14px' }}>Kliknij przycisk "📋 Pokaż użytkowników" aby wczytać listę z bazy danych.</div>
              <div style={{ fontSize: '12px', marginTop: '8px', opacity: 0.7 }}>To oszczędza zapytania Firebase gdy nie potrzebujesz przeglądać użytkowników.</div>
            </div>
          ) : users.length === 0 ? (
            <div style={{ color: '#6b7280' }}>Brak użytkowników.</div>
          ) : (
            (() => {
              // Użyj wyników wyszukiwania lub normalnej listy
              const displayUsers = isInSearchMode ? searchResults : users
              
              if (isSearching) {
                return (
                  <div style={{ color: '#6b7280', textAlign: 'center', padding: '20px' }}>
                    🔍 Wyszukiwanie użytkowników...
                  </div>
                )
              }
              
              if (isInSearchMode && searchResults.length === 0 && searchTerm.trim()) {
                return (
                  <div style={{ color: '#6b7280' }}>
                    Nie znaleziono użytkowników pasujących do wyszukiwania "{searchTerm}".
                    <br />
                    <small>Przeszukano załadowane dane i całą bazę danych.</small>
                  </div>
                )
              }
              
              return (
                <>
                  <div className="users-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
                    {displayUsers.map((u) => (
                      <UserCard key={u.id} user={u} onUpdated={refreshUsers} onShowLoginHistory={(user) => {
                        setSelectedUser(user)
                        setShowLoginHistory(true)
                      }} />
                    ))}
                  </div>
                  {isInSearchMode && searchResultsSource && (
                    <div style={{ marginTop: 12, textAlign: 'center', fontSize: 12, color: '#6b7280' }}>
                      {searchResultsSource === 'cache' 
                        ? '💾 Wyniki z załadowanych danych (szybkie)' 
                        : '📶 Wyniki z pełnej bazy danych (dokładne)'
                      }
                    </div>
                  )}
                </>
              )
            })()
          )}
          
          {/* Przycisk załaduj więcej użytkowników */}
          {hasMoreUsers && !isInSearchMode && (
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <button
                className="btn btn-secondary"
                onClick={loadMoreUsers}
                disabled={loadingMoreUsers}
              >
                {loadingMoreUsers ? 'Załadowanie...' : 'Załaduj więcej użytkowników'}
              </button>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                Wyświetlanych: {users.length} użytkowników
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )

  const renderMaterialsView = () => (
    <div className="admin-settings">
      <div className="admin-view-header">
        <button className="btn-back" onClick={() => setCurrentView('dashboard')}>
          ← Powrót do panelu głównego
        </button>
        <h3>Materiały Wspólne</h3>
      </div>

      <div className="admin-form" style={{ maxWidth: 900 }}>
        <div className="form-group" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <div style={{ fontWeight: 600 }}>Prześlij plik (tylko admin)</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>Udostępnione pliki będą widoczne dla klientów w Panelu Klienta</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label className="btn btn-secondary" style={{ cursor: (isUploading || !isLoggedIn) ? 'not-allowed' : 'pointer', opacity: !isLoggedIn ? 0.6 : 1 }}>
              {isUploading ? 'Wysyłanie...' : 'Wybierz plik'}
              <input type="file" onChange={onUploadFile} style={{ display: 'none' }} disabled={isUploading || !isLoggedIn} />
            </label>
            {!isLoggedIn && (
              <span style={{ color: '#6b7280', fontSize: 12 }}>Zaloguj się jako admin, aby przesyłać pliki</span>
            )}
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Lista materiałów</div>
          {materials.length === 0 ? (
            <div style={{ color: '#6b7280' }}>Brak materiałów.</div>
          ) : (
            <div className="materials-list" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
              {materials.map(m => (
                <div key={m.id} className="material-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f9fafb', padding: '10px 12px', borderRadius: 8 }}>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <div style={{ fontWeight: 600 }}>{m.name}</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>{m.type} • {(m.size / 1024).toFixed(1)} KB</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <a className="btn btn-secondary" href={m.url} target="_blank" rel="noreferrer">Pobierz</a>
                    <button className="btn btn-secondary" onClick={() => onDeleteMaterial(m)} disabled={!isLoggedIn} title={!isLoggedIn ? 'Wymagane logowanie admina' : undefined}>Usuń</button>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {hasMoreMaterials && (
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <button
                className="btn btn-secondary"
                onClick={loadMoreMaterials}
                disabled={loadingMoreMaterials}
              >
                {loadingMoreMaterials ? 'Załadowanie...' : 'Załaduj więcej materiałów'}
              </button>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                Wyświetlanych: {materials.length} materiałów
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )

  const UserCard = ({ user, onUpdated, onShowLoginHistory }: { 
    user: any; 
    onUpdated: () => Promise<void> | void;
    onShowLoginHistory: (user: { id: string; username: string }) => void;
  }) => {
    const [email, setEmail] = useState(user.email || '')
    const [username, setUsername] = useState(user.username || '')
    const [companyName, setCompanyName] = useState(user.companyName || '')
    const [password, setPassword] = useState('')
    const [saving, setSaving] = useState(false)
    const [inlineErrors, setInlineErrors] = useState<{ email?: string; username?: string }>({})
    const [checking, setChecking] = useState<{ email?: boolean; username?: boolean }>({})
    
    return (
      <div 
        className="config-card"
        style={{ 
          background: user.deleted ? '#fef2f2' : '#ffffff',
          border: user.deleted ? '2px solid #fecaca' : '2px solid #e5e7eb',
          minHeight: 'auto'
        }}
      >
        {/* Header Section */}
        <div className="config-header" style={{ marginBottom: 16 }}>
          <h3 style={{ 
            color: user.deleted ? '#b91c1c' : '#8B4513', 
            marginBottom: 8,
            textDecoration: user.deleted ? 'line-through' : 'none'
          }}>
            {user.username} {user.deleted && '(USUNIĘTY)'}
          </h3>
          <div style={{ 
            fontSize: 14, 
            color: '#6b7280', 
            marginBottom: 6,
            fontWeight: 500,
            lineHeight: 1.4
          }}>
            <span style={{ color: '#374151' }}>Email:</span> {user.email}
            <br />
            {user.companyName && (
              <>
                <span style={{ color: '#374151' }}>Firma:</span> {user.companyName}
                <br />
              </>
            )}
            <span style={{ color: '#374151' }}>ID:</span> <span style={{ 
              fontSize: 12, 
              color: '#9ca3af',
              fontFamily: 'monospace',
              background: '#f3f4f6',
              padding: '2px 6px',
              borderRadius: 3,
              marginLeft: 4
            }}>{user.id}</span>
            <br />
            <span style={{ color: '#374151' }}>Rola:</span> <span style={{ 
              fontSize: 13, 
              color: user.role === 'admin' ? '#dc2626' : '#059669',
              fontWeight: 600,
              marginLeft: 4
            }}>
              {user.role || 'client'}
            </span>
          </div>
        </div>

        {/* Edit Section - only for active users */}
        {!user.deleted && (
          <div className="config-details" style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 8 }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: 4, color: '#374151' }}>
                Login:
              </label>
              <input
                type="text"
                placeholder="Login"
                value={username}
                onChange={e => { setUsername(e.target.value); setInlineErrors(prev => ({ ...prev, username: undefined })) }}
                onBlur={async () => {
                  const v = username.trim()
                  const orig = (user.username || '').trim()
                  if (!v || v === orig) return
                  setChecking(prev => ({ ...prev, username: true }))
                  try {
                    const taken = await ProfilesService.isUsernameTaken(v, user.id)
                    setInlineErrors(prev => ({ ...prev, username: taken ? 'Login jest już zajęty.' : undefined }))
                  } finally { setChecking(prev => ({ ...prev, username: false })) }
                }}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '13px'
                }}
              />
              {inlineErrors.username && <div style={{ color: '#b91c1c', fontSize: 11, marginTop: 2 }}>{inlineErrors.username}</div>}
            </div>

            <div style={{ marginBottom: 8 }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: 4, color: '#374151' }}>
                Nazwa firmy:
              </label>
              <input
                type="text"
                placeholder="Nazwa firmy (opcjonalnie)"
                value={companyName}
                onChange={e => setCompanyName(e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '13px'
                }}
              />
            </div>

            <div style={{ marginBottom: 8 }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: 4, color: '#374151' }}>
                E-mail:
              </label>
              <input
                type="email"
                placeholder="E-mail"
                value={email}
                onChange={e => { setEmail(e.target.value); setInlineErrors(prev => ({ ...prev, email: undefined })) }}
                onBlur={async () => {
                  const v = email.trim()
                  const orig = (user.email || '').trim()
                  if (!v || v === orig) return
                  if (!isValidEmail(v)) { setInlineErrors(prev => ({ ...prev, email: 'Nieprawidłowy format e‑mail.' })); return }
                  setChecking(prev => ({ ...prev, email: true }))
                  try {
                    const taken = await ProfilesService.isEmailTaken(v, user.id)
                    setInlineErrors(prev => ({ ...prev, email: taken ? 'Adres e‑mail jest już zajęty.' : undefined }))
                  } finally { setChecking(prev => ({ ...prev, email: false })) }
                }}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '13px'
                }}
              />
              {inlineErrors.email && <div style={{ color: '#b91c1c', fontSize: 11, marginTop: 2 }}>{inlineErrors.email}</div>}
            </div>

            <div style={{ marginBottom: 8 }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: 4, color: '#374151' }}>
                Nowe hasło:
              </label>
              <input 
                type="password" 
                placeholder="Nowe hasło" 
                value={password} 
                onChange={e => setPassword(e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '13px'
                }}
              />
            </div>
          </div>
        )}

        {/* Actions Section */}
        <div className="config-actions" style={{ 
          display: 'grid', 
          gridTemplateColumns: user.deleted ? 'repeat(3, 1fr)' : 'repeat(3, 1fr)', 
          gap: 8 
        }}>
          {user.deleted ? (
            // Actions for deleted users
            <>
              <div className="config-action-wrapper">
                <button 
                  className="btn btn-compact"
                  style={{ 
                    backgroundColor: '#D2B48C', 
                    color: '#333', 
                    border: 'none',
                    padding: '6px 10px',
                    fontSize: '11px',
                    borderRadius: '0px'
                  }}
                  onClick={async () => {
                    if (!confirm(`Przywrócić użytkownika "${user.username}"?\n\nUżytkownik będzie mógł ponownie się zalogować.\n\nKliknij OK aby przywrócić, Anuluj aby anulować.`)) return
                    try {
                      await ProfilesService.restoreUser(user.id)
                      alert(`✅ UŻYTKOWNIK PRZYWRÓCONY\n\nUżytkownik "${user.username}" może się teraz zalogować.`)
                      await onUpdated()
                    } catch (err) {
                      alert('Błąd przywracania: ' + translateFirebaseError(err as any))
                    }
                  }}
                  title="Przywróć użytkownika"
                >
                  <span style={{ fontSize: '16px' }}>🔓</span>
                </button>
                <span className="btn-label">Przywróć</span>
              </div>
              <div className="config-action-wrapper">
                <button 
                  className="btn btn-compact"
                  style={{ 
                    backgroundColor: '#D2B48C', 
                    color: '#333', 
                    border: 'none',
                    padding: '6px 10px',
                    fontSize: '11px',
                    borderRadius: '0px'
                  }}
                  onClick={async () => {
                    if (!confirm(`⚠️ PERMANENTNE USUNIĘCIE\n\nCzy na pewno CAŁKOWICIE usunąć użytkownika "${user.username}"?\n\n🔥 UWAGA: Ta operacja jest NIEODWRACALNA!\n• Profil zostanie całkowicie usunięty z bazy danych\n• Użytkownik zostanie usunięty z Firebase Auth\n• Użytkownik zniknie całkowicie z systemu\n\nKliknij OK aby PERMANENTNIE usunąć, Anuluj aby anulować.`)) return
                    try {
                      // Delete login history first
                      try {
                        await LoginHistoryService.deleteUserLoginHistory(user.id)
                        console.log('Successfully deleted login history for user:', user.id)
                      } catch (historyErr) {
                        console.warn('Failed to delete login history:', historyErr)
                        // Continue with user deletion even if history deletion fails
                      }
                      
                      const result = await ProfilesService.permanentlyDeleteUser(user.id)
                      
                      if (result.profileDeleted) {
                        if (result.authDeleted) {
                          alert(`🔥 UŻYTKOWNIK CAŁKOWICIE USUNIĘTY\n\n• Profil: usunięty z bazy danych\n• Firebase Auth: usunięte (${result.method})\n• Historia logowań: usunięta\n\nUżytkownik całkowicie zniknął z systemu.`)
                        } else {
                          alert(`⚠️ CZĘŚCIOWE USUNIĘCIE\n\n• Profil: usunięty z bazy danych\n• Firebase Auth: BŁĄD usuwania (${result.method})\n• Historia logowań: usunięta\n\nSprawdź logi i usuń ręcznie z Firebase Console → Authentication.`)
                        }
                      } else {
                        alert(`❌ BŁĄD USUWANIA\n\nNie udało się usunąć profilu użytkownika.`)
                      }
                      
                      await onUpdated()
                    } catch (err) {
                      alert('Błąd permanentnego usuwania: ' + translateFirebaseError(err as any))
                    }
                  }}
                  title="Usuń na zawsze"
                >
                  <span style={{ fontSize: '16px' }}>🔥</span>
                </button>
                <span className="btn-label">Usuń na zawsze</span>
              </div>
              <div className="config-action-wrapper">
                <button
                  className="btn btn-compact"
                  style={{ 
                    backgroundColor: '#4CAF50', 
                    color: 'white', 
                    border: 'none',
                    padding: '6px 10px',
                    fontSize: '11px',
                    borderRadius: '0px'
                  }}
                  onClick={() => onShowLoginHistory({ id: user.id, username: user.username })}
                  title="Historia logowań"
                >
                  <span style={{ fontSize: '16px' }}>📊</span>
                </button>
                <span className="btn-label">Historia logowań</span>
              </div>
              <div className="config-action-wrapper">
                <button
                  className="btn btn-compact"
                  style={{ 
                    backgroundColor: '#FF6B35', 
                    color: 'white', 
                    border: 'none',
                    padding: '6px 10px',
                    fontSize: '11px',
                    borderRadius: '0px'
                  }}
                  onClick={async () => {
                    if (confirm(`Czy na pewno chcesz wyczyścić historię logowań użytkownika ${user.username}?`)) {
                      try {
                        await LoginHistoryService.deleteUserLoginHistory(user.id)
                        alert('✅ Historia logowań została usunięta')
                      } catch (error) {
                        console.error('Błąd usuwania historii logowań:', error)
                        alert('❌ Błąd podczas usuwania historii logowań')
                      }
                    }
                  }}
                  title="Wyczyść historię logowań"
                >
                  <span style={{ fontSize: '16px' }}>🗑️</span>
                </button>
                <span className="btn-label">Wyczyść historię</span>
              </div>
            </>
          ) : (
            // Actions for active users
            <>
              <div className="config-action-wrapper">
                <button
                  className="btn btn-compact"
                  style={{ 
                    backgroundColor: '#D2B48C', 
                    color: '#333', 
                    border: 'none',
                    padding: '6px 10px',
                    fontSize: '11px',
                    borderRadius: '0px'
                  }}
                  disabled={
                    saving || !!inlineErrors.email || !!inlineErrors.username || !!checking.email || !!checking.username || (email ? !isValidEmail(email) : false)
                  }
                  onClick={async () => {
                    if (email && !isValidEmail(email)) { alert('Podaj poprawny adres e‑mail.'); return }
                    setSaving(true)
                    try {
                      // Basic uniqueness via profiles store
                      if (email && email.trim() !== (user.email || '').trim()) {
                        if (await ProfilesService.isEmailTaken(email.trim(), user.id)) { alert('Adres e‑mail jest już zajęty.'); setSaving(false); return }
                      }
                      if (username && username.trim() !== (user.username || '').trim()) {
                        if (await ProfilesService.isUsernameTaken(username.trim(), user.id)) { alert('Login jest już zajęty.'); setSaving(false); return }
                      }
                      
                      try {
                        // Try Cloud Functions if available
                        if (email || password || username) {
                          console.log('Wywołuję updateAuthUser z danymi:', { uid: user.id, email: email || undefined, password: password ? 'UKRYTE' : undefined, username: username || undefined })
                          await AdminFunctions.updateAuthUser({ uid: user.id, email: email || undefined, password: password || undefined, username: username || undefined })
                          console.log('updateAuthUser zakończone pomyślnie')
                          if (password) {
                            alert('✅ Dane zaktualizowane (włącznie z hasłem w Firebase Auth)')
                          }
                        }
                      } catch (funcErr) {
                        console.error('Błąd Cloud Functions:', funcErr)
                        alert(`❌ Błąd aktualizacji: ${(funcErr as any)?.message || funcErr}`)
                        setSaving(false)
                        return
                      }
                      
                      // Always update profile in Firestore
                      const updates: any = {}
                      if (email) updates.email = email
                      if (username) updates.username = username
                      if (companyName !== (user.companyName || '')) updates.companyName = companyName.trim() || undefined
                      
                      if (Object.keys(updates).length > 0) {
                        await ProfilesService.updateProfile(user.id, updates)
                      }
                      
                      await onUpdated()
                      setPassword('')
                    } catch (err) {
                      alert('Błąd aktualizacji: ' + (err as any)?.message)
                    } finally {
                      setSaving(false)
                    }
                  }}
                  title="Zapisz zmiany"
                >
                  <span style={{ fontSize: '16px' }}>{saving ? '⏳' : '💾'}</span>
                </button>
                <span className="btn-label">{saving ? 'Zapisywanie...' : 'Zapisz'}</span>
              </div>
              <div className="config-action-wrapper">
                <button 
                  className="btn btn-compact"
                  style={{ 
                    backgroundColor: '#D2B48C', 
                    color: '#333', 
                    border: 'none',
                    padding: '6px 10px',
                    fontSize: '11px',
                    borderRadius: '0px'
                  }}
                  onClick={async () => {
                    if (!confirm(`Czy na pewno usunąć użytkownika "${user.username}"?\n\nUWAGA: Użytkownik zostanie zablokowany i nie będzie mógł się zalogować.\n\nKliknij OK aby usunąć, Anuluj aby wrócić.`)) return
                    try {
                      // ALWAYS use soft deletion to prevent user from logging back in
                      // This ensures that even if Firebase Auth account still exists,
                      // the user will be blocked during login process
                      await ProfilesService.markAsDeleted(user.id)
                      console.log('Successfully marked user as deleted (soft deletion)')
                      
                      // Also delete login history
                      try {
                        await LoginHistoryService.deleteUserLoginHistory(user.id)
                        console.log('Successfully deleted login history for user:', user.id)
                      } catch (historyErr) {
                        console.warn('Failed to delete login history:', historyErr)
                        // Don't fail the whole operation if history deletion fails
                      }
                      
                      // Soft delete - NIE usuwamy z Firebase Auth
                      alert(`✅ UŻYTKOWNIK ZABLOKOWANY\n\n• Firebase Auth: pozostaje (można przywrócić)\n• Profil: oznaczony jako usunięty\n• Historia logowań: usunięta\n\nUżytkownik NIE MOŻE się zalogować (będzie automatycznie wylogowywany).`)
                      
                      await onUpdated()
                    } catch (err) {
                      alert('Błąd usuwania: ' + translateFirebaseError(err as any))
                    }
                  }}
                  title="Usuń użytkownika"
                >
                  <span style={{ fontSize: '16px' }}>❌</span>
                </button>
                <span className="btn-label">Usuń</span>
              </div>
              <div className="config-action-wrapper">
                <button 
                  className="btn btn-compact"
                  style={{ 
                    backgroundColor: '#D2B48C', 
                    color: '#333', 
                    border: 'none',
                    padding: '6px 10px',
                    fontSize: '11px',
                    borderRadius: '0px'
                  }}
                  onClick={() => onShowLoginHistory({ id: user.id, username: user.username })}
                  title="Historia logowań"
                >
                  <span style={{ fontSize: '16px' }}>📊</span>
                </button>
                <span className="btn-label">Historia logowań</span>
              </div>
            </>
          )}
        </div>
      </div>
    )
  }

  const renderCurrentView = () => {
    switch (currentView) {
      case 'dashboard':
        return renderDashboard()
      case 'add-fireplace':
        return renderAddFireplace()
      case 'settings':
        return renderSettingsView()
      case 'users':
        return renderUsersView()
      case 'materials':
        return renderMaterialsView()
      default:
        return renderDashboard()
    }
  }

  return (
    <>
      {isOpen && <FirestoreStatsWidget />}
      <div className={`admin-panel ${isOpen ? 'open' : ''}`}>
        <div className="admin-panel-header">
          <h2>Panel Administracyjny</h2>
          {isLoggedIn && (
            <button className="admin-logout" onClick={onLogout} title="Wyloguj" style={{ marginRight: 8 }}>
              Wyloguj
            </button>
          )}
          <button className="admin-close" onClick={onClose}>✕</button>
        </div>

        <div className="admin-panel-content">
          {renderCurrentView()}
      </div>

      {/* Login History Modal */}
      {selectedUser && (
        <LoginHistoryModal
          isOpen={showLoginHistory}
          onClose={() => {
            setShowLoginHistory(false)
            setSelectedUser(null)
          }}
          userId={selectedUser.id}
          username={selectedUser.username}
        />
      )}
      </div>
    </>
  )
}

export default AdminPanel
